import styles from './Modal.module.scss';
import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Authorization, TrelloPowerUpContext } from '@powerupsclub/trello';
import { PaymentsContext } from '@powerupsclub/payments';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button';
import Form, { Field } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Poll, PollOption } from '../../types/models';
import { Checkbox } from '@atlaskit/checkbox';
import { addPollToCard } from '../../api/pollsInPluginData';
import { FeedbackButton } from '@powerupsclub/feedback';

import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { resizeIframe } from '../../api/resizeIframe';
import { RecentPollsList } from './RecentPollsList';

const defaultPollOption: PollOption = {
  value: '',
  idMemberVotes: [],
};

export const Modal = () => {
  const {
    isLoading: isLoadingTrello,
    isAuthorized,
    trelloIframe,
  } = useContext(TrelloPowerUpContext);
  const {
    isLoading: isLoadingPayments,
    subscription,
    trialEndDate,
  } = useContext(PaymentsContext);

  const trialHasEnded = trialEndDate ? new Date() > trialEndDate : false;
  const notSubscribed = subscription?.status !== 'active';
  const showPayments = trialHasEnded && notSubscribed;

  const [pollName, setPollName] = useState('');

  const [options, setOptions] = useState<PollOption[]>([
    defaultPollOption,
    defaultPollOption,
  ]);

  const [allowCustomOptions, setAllowCustomOptions] = useState(false);
  const [allowMultipleVotes, setAllowMultipleVotes] = useState(false);

  const [selectedTab, setSelectedTab] = useState(0);

  const isLoading = isLoadingTrello || isLoadingPayments;

  useEffect(() => {
    if (showPayments) {
      trelloIframe.modal({
        url: './payment',
        fullscreen: true,
      });
    }
  }, [showPayments, trelloIframe, isLoading]);

  useLayoutEffect(() => {
    resizeIframe(trelloIframe, '.AddPollModal');
  }, [options, trelloIframe, selectedTab]);

  if (isLoading) {
    return (
      <div className={styles.loadingSpinnerContainer}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <Authorization
        heading="Polls"
        imageSrc="./logo.png"
        headingStyle={{
          fontSize: '16px',
        }}
        imageStyle={{
          maxWidth: '75px',
          maxHeight: '75px',
        }}
      />
    );
  }

  return (
    <div className="AddPollModal">
      <Tabs
        selected={selectedTab}
        onChange={(index) => setSelectedTab(index)}
        id="default"
      >
        <TabList>
          <Tab>New</Tab>
          <Tab>Recent</Tab>
        </TabList>

        <TabPanel>
          <Form
            onSubmit={async (values: any) => {
              if (pollName.length === 0) {
                return;
              }

              const poll: Poll = {
                name: pollName,
                options: options.filter((o) => o.value.length > 0),
                settings: {
                  allowCustomOptions,
                  allowMultipleVotes,
                },
              };

              await addPollToCard(trelloIframe, poll);

              trelloIframe.closePopup();
            }}
          >
            {({ formProps }: any) => (
              <form {...formProps}>
                <Field name="name">
                  {({ fieldProps }: any) => (
                    <Textfield
                      placeholder="What are we voting on?"
                      {...fieldProps}
                      value={pollName}
                      onChange={(e: any) => {
                        setPollName(e.target.value);
                        fieldProps.onChange(e);
                      }}
                    />
                  )}
                </Field>

                {options.map((option, index) => (
                  <Field
                    name={`option${index + 1}`}
                    key={index}
                    defaultValue={option.value}
                  >
                    {({ fieldProps }: any) => (
                      <>
                        <Textfield
                          placeholder={`Option ${index + 1}`}
                          {...fieldProps}
                          onChange={(e: any) => {
                            const newOptions = [...options];
                            newOptions[index] = {
                              ...newOptions[index],
                              value: e.target.value,
                            };
                            setOptions(newOptions);

                            fieldProps.onChange(e);
                          }}
                          elemAfterInput={
                            index > 1 && (
                              <div
                                style={{
                                  marginRight: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  let confirmed = true;

                                  if (option.value?.length > 0) {
                                    confirmed = window.confirm(
                                      'Are you sure you want to remove this option?'
                                    );
                                  }

                                  if (confirmed) {
                                    const newOptions = [...options];
                                    newOptions.splice(index, 1);
                                    setOptions(newOptions);
                                  }
                                }}
                              >
                                <CrossIcon
                                  primaryColor="rgb(94, 108, 132)"
                                  label="delete option"
                                  size="small"
                                />
                              </div>
                            )
                          }
                        />
                      </>
                    )}
                  </Field>
                ))}

                <Button
                  shouldFitContainer
                  style={{ marginTop: '8px' }}
                  onClick={() => setOptions((o) => [...o, defaultPollOption])}
                >
                  Add option
                </Button>

                <Checkbox
                  label="Allow voters to add options"
                  isChecked={allowCustomOptions}
                  onChange={(e) => {
                    setAllowCustomOptions(e.target.checked);
                  }}
                  name="add-custom-options"
                />

                <Checkbox
                  label="Allow multiple votes per person"
                  isChecked={allowMultipleVotes}
                  onChange={(e) => {
                    setAllowMultipleVotes(e.target.checked);
                  }}
                  name="multiple-votes"
                />

                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <Button
                    shouldFitContainer
                    type="submit"
                    appearance="primary"
                    isDisabled={
                      options[0].value.length === 0 &&
                      options[1].value.length === 0
                    }
                  >
                    Add poll to card
                  </Button>
                  <FeedbackButton hideText />
                </div>
              </form>
            )}
          </Form>
        </TabPanel>

        <TabPanel>
          <RecentPollsList
            onSelectPoll={(poll) => {
              setPollName(poll.name);
              setOptions(poll.options);
              setAllowCustomOptions(poll.settings.allowCustomOptions);
              setAllowMultipleVotes(poll.settings.allowMultipleVotes);
              setSelectedTab(0);
            }}
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};
